import React, {useEffect, useState} from "react";
import {
    useBlockNote,
    createReactBlockSpec,
    FormattingToolbar,
    FormattingToolbarController,
    BlockTypeSelect,
    blockTypeSelectItems,
    SuggestionMenuController,
    getDefaultReactSlashMenuItems,
    BasicTextStyleButton,
    CreateLinkButton,
    NestBlockButton,
    UnnestBlockButton,
} from "@blocknote/react";
import "@blocknote/core/style.css";
import {BlockNoteView} from "@blocknote/mantine";

import "@blocknote/core/fonts/inter.css";
import "@blocknote/mantine/style.css";
import {BlockNoteSchema, defaultBlockSpecs, defaultInlineContentSpecs, defaultStyleSpecs, defaultProps} from "@blocknote/core";

const markdownSchema = BlockNoteSchema.create({
    blockSpecs: {
        paragraph: defaultBlockSpecs.paragraph,
        bulletListItem: defaultBlockSpecs.bulletListItem,
    },
    inlineContentSpecs: {
        link: defaultInlineContentSpecs.link,
    },
    styleSpecs: {
        bold: defaultStyleSpecs.bold,
        italic: defaultStyleSpecs.italic,
    },
});

// Create custom heading block that only allows H2 and H3
const RestrictedHeading = createReactBlockSpec(
    {
        type: "heading",
        propSchema: {
            ...defaultProps,
            level: {
                default: 2,
                values: [2, 3], // Only H2 and H3
            },
        },
        content: "inline",
    },
    {
        render: (props) => {
            const Tag = `h${props.block.props.level}`;
            return React.createElement(Tag, { ref: props.contentRef });
        },
    }
);

const htmlSchema = BlockNoteSchema.create({
    blockSpecs: {
        paragraph: defaultBlockSpecs.paragraph,
        heading: RestrictedHeading,
        bulletListItem: defaultBlockSpecs.bulletListItem,
        numberedListItem: defaultBlockSpecs.numberedListItem,
    },
    inlineContentSpecs: {
        link: defaultInlineContentSpecs.link,
    },
    styleSpecs: {
        bold: defaultStyleSpecs.bold,
        italic: defaultStyleSpecs.italic,
        underline: defaultStyleSpecs.underline,
    },
});

function RichEditorBase({onChange, value, schema, parseContent, convertContent, className, customStyles, isHtmlMode}) {
    const editor = useBlockNote({
        schema: schema,
        inlineContentSpecs: {
            bold: true,
            italic: true,
            link: true,
            ...(schema === htmlSchema && { underline: true }),
        },
    });

    let editorOnChange = async (editorState) => {
        const content = await convertContent(editor);
        onChange(content);
    };

    useEffect(() => {
        if (value) {
            async function loadInitialContent() {
                const blocks = await parseContent(editor, value);
                editor.replaceBlocks(editor.document, blocks);
            }
            loadInitialContent();
        }
    }, [editor, value]);

    // Custom heading items for slash menu
    const getCustomSlashMenuItems = (editor) => {
        const defaultItems = getDefaultReactSlashMenuItems(editor);
        
        if (isHtmlMode) {
            // Filter out default heading items, emoji, and add custom H2/H3
            const filteredItems = defaultItems.filter(item => 
                item.title !== "Heading" && 
                item.title !== "Emoji" &&
                !item.title.toLowerCase().includes("emoji")
            );
            
            const customHeadingItems = [
                {
                    title: "Heading 2",
                    onItemClick: () => {
                        editor.updateBlock(editor.getTextCursorPosition().block, {
                            type: "heading",
                            props: { level: 2 }
                        });
                    },
                    aliases: ["h2", "heading2"],
                    group: "Headings",
                    subtext: "Insert a level 2 heading"
                },
                {
                    title: "Heading 3", 
                    onItemClick: () => {
                        editor.updateBlock(editor.getTextCursorPosition().block, {
                            type: "heading",
                            props: { level: 3 }
                        });
                    },
                    aliases: ["h3", "heading3"],
                    group: "Headings",
                    subtext: "Insert a level 3 heading"
                }
            ];
            
            return [...filteredItems, ...customHeadingItems];
        }
        
        return defaultItems;
    };

    // Custom block type select items for formatting toolbar
    const getCustomBlockTypeSelectItems = (editor) => {
        if (isHtmlMode) {
            return blockTypeSelectItems(editor.dictionary).filter(item => {
                if (item.type === "heading") {
                    return item.props?.level === 2 || item.props?.level === 3;
                }
                return true;
            });
        }
        return blockTypeSelectItems(editor.dictionary);
    };

    return (
        <div className={className}>
            <BlockNoteView
                theme="light"
                onChange={editorOnChange}
                style={{ 
                    minHeight: '60px', 
                    border: "1px solid #d9d9d9", 
                    borderRadius: "2px", 
                    padding: "4px 11px"
                }}
                sideMenu={false}
                emojiPicker={false}
                editor={editor}
                formattingToolbar={false}
                slashMenu={false}
            >
                <FormattingToolbarController
                    formattingToolbar={() => (
                        <FormattingToolbar>
                            {isHtmlMode && (
                                <BlockTypeSelect
                                    key={"blockTypeSelect"}
                                    items={getCustomBlockTypeSelectItems(editor)}
                                />
                            )}
                            <BasicTextStyleButton
                                key={"boldStyleButton"}
                                basicTextStyle={"bold"}
                            />
                            <BasicTextStyleButton
                                key={"italicStyleButton"}
                                basicTextStyle={"italic"}
                            />
                            {schema === htmlSchema && (
                                <BasicTextStyleButton
                                    key={"underlineStyleButton"}
                                    basicTextStyle={"underline"}
                                />
                            )}
                            <CreateLinkButton key={"createLinkButton"} />
                            <NestBlockButton key={"nestBlockButton"} />
                            <UnnestBlockButton key={"unnestBlockButton"} />
                        </FormattingToolbar>
                    )}
                />
                {isHtmlMode && (
                    <SuggestionMenuController
                        triggerCharacter={"/"}
                        getItems={async (query) =>
                            getCustomSlashMenuItems(editor).filter((item) =>
                                item.title.toLowerCase().includes(query.toLowerCase()) ||
                                item.aliases?.some(alias => alias.toLowerCase().includes(query.toLowerCase()))
                            )
                        }
                    />
                )}
            </BlockNoteView>
            {customStyles && (
                <style dangerouslySetInnerHTML={{
                    __html: customStyles
                }} />
            )}
        </div>
    );
}

class RichEditorMarkdown extends React.Component {
    parseContent = async (editor, value) => {
        return await editor.tryParseMarkdownToBlocks(value);
    };

    convertContent = async (editor) => {
        return await editor.blocksToMarkdownLossy(editor.document);
    };

    render() {
        return (
            <RichEditorBase
                {...this.props}
                schema={markdownSchema}
                parseContent={this.parseContent}
                convertContent={this.convertContent}
                className="rich-editor-markdown"
                isHtmlMode={false}
            />
        );
    }
}

class RichEditorHtml extends React.Component {
    parseContent = async (editor, value) => {
        return await editor.tryParseHTMLToBlocks(value);
    };

    convertContent = async (editor) => {
        return await editor.blocksToHTMLLossy(editor.document);
    };

    getCustomStyles = () => {
        return `
            .rich-editor-html .bn-editor,
            .rich-editor-html .bn-editor * {
                font-family: Inter var,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica Neue,Arial,Noto Sans,sans-serif,"Apple Color Emoji","Segoe UI Emoji",Segoe UI Symbol,"Noto Color Emoji" !important;
            }
            .rich-editor-html .bn-editor h2 {
                font-size: 1.25rem !important;
                line-height: 1.75rem !important;
                font-weight: 600 !important;
                color: #27272a !important;
                margin-top: 1rem !important;
                margin-bottom: 0.5rem !important;
            }
            .rich-editor-html .bn-editor h3 {
                font-size: 1rem !important;
                line-height: 1.5rem !important;
                font-weight: 600 !important;
                color: #27272a !important;
                margin-top: 1rem !important;
                margin-bottom: 0.5rem !important;
            }
            .rich-editor-html .bn-editor p {
                font-size: 1rem !important;
                line-height: 1.5rem !important;
                margin-bottom: 0.5rem !important;
            }
            .rich-editor-html .bn-editor ul {
                margin-left: 1rem !important;
                list-style-type: disc !important;
            }
            .rich-editor-html .bn-editor ol {
                margin-left: 1rem !important;
                list-style-type: decimal !important;
            }
        `;
    };

    render() {
        return (
            <RichEditorBase
                {...this.props}
                schema={htmlSchema}
                parseContent={this.parseContent}
                convertContent={this.convertContent}
                className="rich-editor-html"
                customStyles={this.getCustomStyles()}
                isHtmlMode={true}
            />
        );
    }
}

// Create a functional component version for backward compatibility
function BoldLinkOnlyEditor({onChange, value}) {
    return <RichEditorMarkdown onChange={onChange} value={value} />;
}

export { RichEditorBase, RichEditorHtml, RichEditorMarkdown, BoldLinkOnlyEditor };
